/**
 * Upsell Page — ₹2,499 Session Pass (Task 7)
 * Shown after ₹99 payment is confirmed.
 * Pulls ATS score dynamically from analysis_id.
 */
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Star, ArrowRight, Loader2, Clock, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import LPFooter from "@/components/LPFooter";
import { getMetaAttributionPayload } from "@/utils/metaPixel";
import { useMarketConfig, price as fmtPrice } from "@/utils/marketConfig";
import { reviewsFor } from "@/data/testimonials";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERABLES = [
  "60-minute live 1-on-1 with a certified career expert",
  "Complete resume rewrite during the session — you watch it happen",
  "ATS score guaranteed to reach 85+ or we refund you",
  "Job targeting strategy mapped to your specific role & salary goal",
  "30-day email support after the session for follow-up fixes",
];

export default function UpsellPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const p = (amt) => (mktReady ? fmtPrice(mkt, amt) : "…");
  const analysisId = searchParams.get("analysis_id") || "";
  // Region-matched testimonial (same market-keyed set + headshots as the landing
  // wall). Prefer a review with a score/salary jump — most persuasive on the
  // score-guarantee upsell.
  const _reviews = reviewsFor(mkt.market);
  const proof = _reviews.find((r) => /→/.test(r.text)) || _reviews[0];

  const [score, setScore] = useState(null);
  const [category, setCategory] = useState("Needs Work");
  const [loadingScore, setLoadingScore] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    const fetchScore = async () => {
      try {
        const sid = localStorage.getItem("pos_session") || "";
        const res = await axios.get(`${API}/upsell/ats-score`, {
          params: { analysis_id: analysisId },
          headers: { "X-Session-ID": sid, ...getHeaders() },
        });
        setScore(res.data.score);
        setCategory(res.data.category || "Needs Work");
      } catch (e) {
        // Fall back to a REAL stored score if we have one; otherwise leave it
        // null and render a score-less hero. Never fabricate a number — a
        // hardcoded 35 would falsely tell a logged-out / no-analysis visitor
        // their own resume scored 35 and is "being rejected by ATS".
        const stored = sessionStorage.getItem("pos_ats_score");
        const parsed = stored ? parseInt(stored) : NaN;
        if (!Number.isNaN(parsed)) setScore(parsed);
      } finally {
        setLoadingScore(false);
      }
    };
    fetchScore();
  }, [analysisId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBookSession = async () => {
    const sid = localStorage.getItem("pos_session") || "";
    const email = sessionStorage.getItem("pos_hero_email") || "";
    // A season-pass buyer must be identifiable so the purchase can be attributed
    // and delivered. If they're not logged in and we have no captured email, send
    // them through sign-in first (returning here afterwards).
    if (!user && !(email.includes("@"))) {
      toast("Please sign in to continue to checkout.");
      navigate(`/auth?return=${encodeURIComponent(`/upsell/session-pass?analysis_id=${analysisId}`)}`);
      return;
    }
    setCheckingOut(true);
    try {
      const res = await axios.post(
        `${API}/upsell/create-session-pass-checkout`,
        {
          analysis_id: analysisId,
          origin_url: window.location.origin,
          email,
          ...getMetaAttributionPayload(),
        },
        { headers: { "X-Session-ID": sid, ...getHeaders() } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout — please try again.");
      setCheckingOut(false);
    }
  };

  const handleRemindLater = async () => {
    try {
      const sid = localStorage.getItem("pos_session") || "";
      const email = sessionStorage.getItem("pos_hero_email") || "";
      const name = sessionStorage.getItem("pos_hero_name") || "";
      if (email) {
        await axios.post(
          `${API}/upsell/day3-reminder`,
          { analysis_id: analysisId, email, name },
          { headers: { "X-Session-ID": sid } }
        );
      }
    } catch (e) { /* silent */ }
    navigate(`/results`);
  };

  const handleContinue = () => {
    navigate("/results");
  };

  const scoreColor =
    score === null ? "#6b7280"
    : score <= 40 ? "#ef4444"
    : score <= 70 ? "#f59e0b"
    : "#22c55e";

  return (
    <div className="ap-root min-h-screen flex flex-col">
      {/* Stripped header — logo only (this route does NOT use the global navbar) */}
      <header className="border-b" style={{ borderColor: "var(--ap-line)", background: "var(--ap-card)" }}>
        <div className="max-w-3xl mx-auto flex items-center px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "var(--ap-fire)" }}>
              <span className="text-white font-extrabold text-sm ap-display">P</span>
            </div>
            <span className="ap-display font-extrabold text-[var(--ap-ink)] text-lg">
              Placement<span className="ap-grad-text">OS</span>
            </span>
          </div>
        </div>
      </header>

      <main className="ap-mesh ap-grid-overlay relative flex-1">
        <div className="max-w-3xl mx-auto px-5 py-12 sm:py-16">
          {/* Hero score section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 ap-chip ap-rise ap-rise-1 mb-6"
              style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)" }}>
              <Clock className="w-4 h-4" />
              <span className="font-semibold">Special offer — expires in 24 hours</span>
            </div>

            {loadingScore ? (
              <div className="flex items-center justify-center gap-3 mb-4">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--ap-violet)" }} />
                <span className="text-[var(--ap-ink-2)] text-xl">Loading your score...</span>
              </div>
            ) : score !== null ? (
              <>
                <h1 className="ap-display ap-rise ap-rise-2 font-extrabold leading-[1.05] tracking-tight mb-4 text-[var(--ap-ink)]"
                  style={{ fontSize: "clamp(32px,5vw,52px)" }}>
                  Your score is{" "}
                  <span style={{ color: scoreColor }} className="ap-display font-black" >
                    {score}/100
                  </span>
                  .
                </h1>
                <p className="ap-rise ap-rise-3 text-lg sm:text-xl text-[var(--ap-muted)] mb-2 leading-relaxed">
                  Let an expert <strong className="text-[var(--ap-ink)]">fix it with you</strong> — live, in 60 minutes.
                </p>
              </>
            ) : (
              <>
                <h1 className="ap-display ap-rise ap-rise-2 font-extrabold leading-[1.05] tracking-tight mb-4 text-[var(--ap-ink)]"
                  style={{ fontSize: "clamp(32px,5vw,52px)" }}>
                  Fix your resume with an <span className="ap-grad-text">expert</span> — live.
                </h1>
                <p className="ap-rise ap-rise-3 text-lg sm:text-xl text-[var(--ap-muted)] mb-2 leading-relaxed">
                  A certified career expert rewrites your resume with you, in 60 minutes.
                </p>
              </>
            )}

            {!loadingScore && score !== null && score <= 70 && (
              <div className="inline-block rounded-xl px-4 py-2.5 mt-4 border"
                style={{ background: "rgba(239,68,68,.08)", borderColor: "rgba(239,68,68,.30)" }}>
                <p className="text-sm font-medium" style={{ color: "#dc2626" }}>
                  {score <= 40
                    ? "⚠️ Critical — ATS filters likely rejecting your resume before any human sees it."
                    : "⚠️ Below average — most top-tier companies filter out resumes below 70."}
                </p>
              </div>
            )}
          </div>

          {/* Pricing card */}
          <div className="ap-card ap-pop overflow-hidden mb-8 p-0" style={{ borderColor: "var(--ap-violet)", borderWidth: 2 }}>
            <div className="px-6 py-4 text-center" style={{ background: "var(--ap-fire)" }}>
              <p className="text-white font-bold text-sm uppercase tracking-[0.14em] ap-display">Expert Session Pass</p>
            </div>

            <div className="p-7 sm:p-9">
              {/* Price anchor */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-1.5">
                  <span className="text-2xl sm:text-3xl font-bold text-[var(--ap-muted)] line-through">{p(mkt.prices.pass_annual_was)}</span>
                  <span className="ap-display text-4xl sm:text-5xl font-black text-[var(--ap-ink)]">{p(mkt.prices.pass_annual)}</span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1"
                  style={{ background: "rgba(22,163,74,.10)", color: "var(--ap-green)" }}>
                  50% off — launch special
                </span>
              </div>

              {/* Deliverables */}
              <div className="space-y-3.5 mb-8">
                {DELIVERABLES.map((d, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[var(--ap-green)] mt-0.5 flex-shrink-0" />
                    <span className="text-[var(--ap-ink-2)] text-[15px] leading-relaxed">{d}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={handleBookSession}
                disabled={checkingOut}
                className="ap-btn ap-btn-fire ap-btn-lg w-full disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Book My Expert Session — {p(mkt.prices.pass_annual)}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Risk reversal */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <Shield className="w-4 h-4 text-[var(--ap-muted)] flex-shrink-0" />
                <p className="text-[var(--ap-muted)] text-xs text-center">
                  Money-back guarantee — if your ATS score doesn't improve, we refund you. No questions asked.
                </p>
              </div>
            </div>
          </div>

          {/* Social proof — region-matched testimonial with a real headshot
              (same market-keyed set as the landing wall). US visitors see a US
              name/city, UK £, etc.; India keeps its own. */}
          {mktReady && proof && (
          <div className="ap-card p-6 mb-10">
            <div className="flex items-start gap-4">
              <div className="relative w-12 h-12 rounded-full flex-shrink-0 overflow-hidden grid place-items-center text-white text-sm font-bold" style={{ background: proof.color }}>
                <span>{proof.avatar}</span>
                <img
                  src={proof.img}
                  alt={proof.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div>
                <div className="flex gap-1 mb-2 text-[var(--ap-orange)]">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-[var(--ap-ink-2)] text-sm leading-relaxed mb-3 italic">"{proof.text}"</p>
                <p className="font-semibold text-sm text-[var(--ap-ink)]">{proof.name}</p>
                <p className="text-[var(--ap-muted)] text-xs">{proof.loc}</p>
              </div>
            </div>
          </div>
          )}

          {/* Bottom links */}
          <div className="text-center space-y-4">
            <button
              onClick={() => navigate("/templates")}
              className="ap-btn ap-btn-ghost ap-btn-md w-full"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              View My Optimized Resume (Vault)
            </button>
            <div>
              <button
                onClick={handleRemindLater}
                className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink)] underline underline-offset-2 transition-colors"
              >
                Not ready? Remind me in 3 days
              </button>
            </div>
            <div className="text-[var(--ap-line)]">•</div>
            <button
              onClick={handleContinue}
              className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink)] transition-colors"
            >
              Continue to My Report →
            </button>
          </div>
        </div>
      </main>
      <LPFooter />
    </div>
  );
}
